import { Badge, Flex, Icon } from '@chakra-ui/react';
import { formatSlot, getCapacityForLessonType, getLessonTypeForCode } from '@opanuj/shared/utils';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { isNil, uniqBy } from 'lodash';
import { Tooltip } from '../../../../components/ui/tooltip';

export const CardStatus = ({ slot, selectedConflictIssue }: { slot: any; selectedConflictIssue?: any }) => {
	const reservationCount = slot?.reservations?.length ?? 0;
	const reservationTooltip = (
		<div>
			{slot.reservations?.map((reservation: any, index: number) => (
				<span key={reservation.reservationId ?? index} style={{ display: 'block', whiteSpace: 'nowrap' }}>
					uczeń: {reservation.student?.name ?? 'Nieznany uczeń'} • klient: {reservation.client?.name ?? 'Nieznany klient'}
				</span>
			))}
		</div>
	);
	const startedLessonStudents = uniqBy(
		slot.lessons?.flatMap((lesson: any) => lesson.students ?? []) ?? [],
		(student: any) => student.client?.id ?? student.client?.uid ?? student.uid ?? student.id ?? student.name,
	);
	const startedLessonTooltip = (
		<div>
			{startedLessonStudents.map((student: any, index: number) => (
				<span key={student.uid ?? student.id ?? index} style={{ display: 'block', whiteSpace: 'nowrap' }}>
					uczeń: {student.name ?? 'Nieznany uczeń'} • klient: {student.client?.name ?? 'Nieznany klient'}
				</span>
			))}
		</div>
	);
	const lessonType = getLessonTypeForCode(slot?.code ?? '');
	const capacity = lessonType ? getCapacityForLessonType(lessonType) - startedLessonStudents.length : undefined;
	const reservationLabel = capacity !== undefined ? `zapis (${reservationCount}/${capacity})` : 'zapis';
	const effectiveConflictIssue = selectedConflictIssue || slot?.issue;
	const conflictReason = effectiveConflictIssue?.reason || slot?.issue?.reason || slot?.isTeacherAvailable?.reason || slot?.isRoomAvailable?.reason;
	const conflictTarget = effectiveConflictIssue?.lesson?.slotData || slot?.issue?.lesson?.slotData || slot?.isTeacherAvailable?.data?.slotData || slot?.isRoomAvailable?.data?.slotData;
	const tooltipContentProps = {
		bg: 'white',
		color: 'gray.800',
		borderWidth: '1px',
		borderColor: 'gray.200',
		borderRadius: 'md',
		fontSize: 'xs',
		boxShadow: 'sm',
		px: 2,
		py: 1,
	};

	const renderConflictIcon = ({ reason }: { reason?: string }) => (
		<Tooltip
			content={reason || 'konflikt'}
			positioning={{ placement: 'top' }}
			contentProps={tooltipContentProps}
		>
			<Icon as={HiOutlineInformationCircle} boxSize={4} color="red.500" aria-label="conflict info" />
		</Tooltip>
	);

	const renderBadge = ({ label, colorPalette, variant='subtle', reason, target, bg, borderColor }: { label: string; colorPalette?: string; variant?: 'outline' | 'subtle' | 'solid'; reason?: string; target?: any; bg?: string; borderColor?: string }) => (
		<Tooltip
			content={reason || (target ? formatSlot(target) : ' konflikt')}
			positioning={{ placement: 'top' }}
			contentProps={tooltipContentProps}
		>
			<Badge colorPalette={colorPalette as any} variant={variant} bg={bg} borderColor={borderColor}>
				{label}
			</Badge>
		</Tooltip>
	);

	return (
		<Flex mr={2} ml="auto" align="center" justify="flex-end" wrap="wrap" gap={1}>
			{reservationCount > 0 && (
				<Tooltip content={reservationTooltip} positioning={{ placement: 'top' }} contentProps={tooltipContentProps}>
					<Badge colorPalette="cyan" variant="subtle" bg="cyan.50">
						{reservationLabel}
					</Badge>
				</Tooltip>
			)}
			{slot.hidden && (
				renderBadge({
					label: 'ukryty',
					colorPalette: 'gray',
					variant: 'subtle',
					reason: 'ręcznie ukryty',
					bg: 'gray.100',
				})
			)}
			{slot.started && (
				<Tooltip content={startedLessonTooltip} positioning={{ placement: 'top' }} contentProps={tooltipContentProps}>
					<Badge colorPalette="blue" variant="subtle" bg="blue.50">
						wystartowany
					</Badge>
				</Tooltip>
			)}
			{slot.isBlocked && (
				renderBadge({
					label: 'niedostępny',
					colorPalette: 'red',
					variant: 'subtle',
					reason: conflictReason || 'slot zablokowany przez istniejącą rezerwację lub rozpoczęte lekcje',
					target: conflictTarget,
					bg: 'red.50',
					borderColor: 'red.200',
				})
			)}
			{selectedConflictIssue && (
				renderConflictIcon({
					reason: conflictReason || 'slot koliduje z wybranym terminem',
				})
			)}
			{slot.isRoomAvailable ? null : (
				renderBadge({
					label: 'Pokój zajęty',
					colorPalette: 'red',
					variant: 'subtle',
					reason: slot.isRoomAvailable?.reason || 'pokój zajęty',
					target: slot.isRoomAvailable?.data?.slotData,
					bg: 'red.50',
					borderColor: 'red.200',
				})
			)}
			{slot.isTeacherAvailable && isNil(slot.lessons) && (
				renderBadge({
					label: 'niedostępny',
					colorPalette: 'yellow',
					variant: 'subtle',
					reason: slot.isTeacherAvailable.reason || 'niedostępny',
					target: slot.isTeacherAvailable.data?.slotData,
					bg: 'yellow.50',
					borderColor: 'yellow.200',
				})
			)}
		</Flex>
	);
};
export default CardStatus;